import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import { groupBy, sum } from "../../services/stats.util";
import Chart from "chart.js/auto";
import type { ChartConfiguration, ChartDataset } from "chart.js";
import { ArticlesInfosFacadeService } from "../../services/article-infos/article-infos-facade.service";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { Widget } from "../../../dashboard/models/dashboard.model";
import { SharedModule } from "../../../../shared/shared.module";
import { PopoverModule } from "../../../../shared/modules/popover/popover.module";
import { ArticleFacadeService } from "../../services/article/article-facade.service";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { parseNumber } from "../../../../shared/utils/number-utils.service";

type Mode = "CA" | "VOLUME";
type ViewType = "PIE" | "LIST";
type Scope = "TOTAL" | "PERTES";

@Component({
  selector: "vl-consos-par-article",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    PopoverModule,
    ChipsToggleComponent,
    ChipsComponent,
    WidgetTitleComponent,
  ],
  templateUrl: "./consos-par-article.component.html",
  styleUrls: ["./consos-par-article.component.scss"],
  host: { class: "widget-component" },
})
export class ConsosParArticleComponent {
  articleInfosFacade = inject(ArticlesInfosFacadeService);
  articleFacade = inject(ArticleFacadeService);
  stats = this.articleFacade.getState().stats;
  metadata = input<EntityMetadata>();
  widget = input<Widget>();

  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("consoChart");
  chart?: Chart<"doughnut", number[], string>;

  // Légende externe (mise à jour via legend.generateLabels)
  legendLabels = signal<
    { label: string; percentage: string; color: string }[] | null
  >(null);

  // Toggles
  viewMode = signal<Mode>("CA");
  viewType = signal<ViewType>("PIE");
  elementsMode = [
    { name: "Valeur", value: "CA" },
    { name: "Volume", value: "VOLUME" },
  ];
  elementsView = [
    { icon: "pie_chart", value: "PIE" },
    { icon: "list", value: "LIST" },
  ];

  viewScope = signal<Scope>("TOTAL");
  elementsScope = [
    { name: "Tout", value: "TOTAL" },
    { name: "Pertes", value: "PERTES" },
  ];

  // Slider Top N
  topCount = signal<number>(8);
  maxRows = computed(() => this.rows().length);

  // Formatters
  private fmtEUR = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
  private fmtINT = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });

  private toNum(x: unknown): number {
    const n = parseNumber(x);
    return Number.isFinite(n) ? n : 0;
  }

  /** Quantité consommée selon le scope :
   *  - TOTAL  => consoProd (fallback consoTheorique)
   *  - PERTES => pertes
   */
  private consoQty(b: any, lossesOnly: boolean): number {
    const consoProd = this.toNum(b?.consoProd);
    const consoTheo = this.toNum(b?.consoTheorique);
    const pertes = this.toNum(b?.pertes);
    const qtyTotal = consoProd > 0 ? consoProd : consoTheo; // fallback
    const qty = lossesOnly ? pertes : qtyTotal;
    return Math.max(0, qty);
  }

  /** Montant consommé = qty(scope) × coutUnitaire */
  private consoAmount(b: any, lossesOnly: boolean): number {
    const cu = this.toNum(b?.coutUnitaire);
    return this.consoQty(b, lossesOnly) * cu;
  }

  // Groupement par article fini (bilan.of.articleId) + % pertes moyen par groupe
  private rows = computed(() => {
    const s = this.stats();
    const bilans = (s?.bilans ?? []) as any[];
    const onlyLosses = this.viewScope() === "PERTES";

    const g = groupBy(bilans, (b: any) => b?.of?.articleId ?? "Inconnu");

    return Object.entries(g).map(([key, es]) => {
      const aid = (es[0] as any)?.of?.articleId;
      const label =
        this.articleInfosFacade.getByArticleId(aid)?.()?.denomination ?? key;

      const qty = sum(es.map((b: any) => this.consoQty(b, onlyLosses)));
      const amount = sum(es.map((b: any) => this.consoAmount(b, onlyLosses)));

      const baseQtyTotal = sum(es.map((b: any) => this.consoQty(b, false)));
      const basePertes = sum(es.map((b: any) => this.toNum(b?.pertes)));
      const lossPct = baseQtyTotal > 0 ? (basePertes / baseQtyTotal) * 100 : 0;

      return { key, label, qty, amount, lossPct, baseQtyTotal };
    });
  });

  // Tri (selon le mode) + Top N
  private sorted = computed(() => {
    const mode = this.viewMode();
    const copy = [...this.rows()];
    copy.sort((a, b) => (mode === "CA" ? b.amount - a.amount : b.qty - a.qty));
    return copy;
  });

  private top = computed(() => {
    const n = Math.max(1, Math.min(this.topCount(), this.maxRows()));
    return this.sorted().slice(0, n);
  });

  // ===== Totaux & « Autres » =====
  private totalAll = computed(() => {
    const mode = this.viewMode();
    return this.rows().reduce(
      (a, x) => a + (mode === "CA" ? x.amount : x.qty),
      0,
    );
  });
  private totalTopOnly = computed(() => {
    const mode = this.viewMode();
    return this.top().reduce(
      (a, x) => a + (mode === "CA" ? x.amount : x.qty),
      0,
    );
  });
  private othersValue = computed(() =>
    Math.max(0, this.totalAll() - this.totalTopOnly()),
  );
  private hasOthers = computed(
    () =>
      this.rows().length >
      Math.max(1, Math.min(this.topCount(), this.maxRows())),
  );

  // Données dynamiques (PIE + LIST)
  private pieLabels = computed(() => {
    const labs = this.top().map((x) => x.label);
    if (this.hasOthers()) labs.push("Autres");
    return labs;
  });

  // ⚠️ public (utilisé par le template pour l'empty state)
  values = computed(() => {
    const vals = this.top().map((x) =>
      this.viewMode() === "CA" ? x.amount : x.qty,
    );
    if (this.hasOthers()) vals.push(this.othersValue());
    return vals;
  });

  private total = computed(() => this.values().reduce((a, b) => a + b, 0)); // = totalAll (inclut « Autres »)
  totalTop = () => this.totalTopOnly();
  totalTopPct = () => {
    const all = this.totalAll();
    return all > 0 ? (this.totalTopOnly() / all) * 100 : 0;
  };

  // Moyenne pondérée des pertes sur le Top N (pondération = baseQtyTotal)
  avgLossPctTop = computed(() => {
    const rows = this.top();
    const denom = sum(rows.map((r) => r.baseQtyTotal));
    if (!denom) return 0;
    const weighted = sum(rows.map((r) => r.lossPct * r.baseQtyTotal));
    return weighted / denom;
  });

  // Lignes pour la liste (avec % basé sur total global)
  listRows = computed(() => {
    const totAll = this.total() || 0;
    const mode = this.viewMode();
    return this.top().map((x) => {
      const value = mode === "CA" ? x.amount : x.qty;
      const pct = totAll > 0 ? (value / totAll) * 100 : 0;
      return { key: x.key, label: x.label, value, pct, lossPct: x.lossPct };
    });
  });

  // ===== Palette dynamique =====
  private makeColors(n: number): string[] {
    const base = [140, 210, 260, 275, 40, 0, 195, 220];
    const out: string[] = [];
    for (let i = 0; i < n; i++) {
      const h =
        base[i % base.length] +
        (i >= base.length ? 12 * Math.floor(i / base.length) : 0);
      out.push(`hsl(${h % 360} 85% 55%)`);
    }
    return out;
  }

  private colors = computed(() => this.makeColors(this.values().length));

  // ===== Helpers =====
  private arrEq<T>(a?: T[] | null, b?: T[] | null): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  constructor() {
    // Clamp du slider (ne pas écraser la valeur utilisateur)
    effect(
      () => {
        // 1) Reset sur changement de stats
        this.stats(); // dépendance voulue
        const max = untracked(() => this.maxRows()) || 1; // ne pas déclencher sur maxRows
        this.topCount.set(Math.min(10, max));
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        // 2) Clamp quand maxRows évolue
        const max = this.maxRows() || 1;
        this.topCount.update((v) => Math.min(Math.max(1, v || 1), max));
      },
      { allowSignalWrites: true },
    );

    // MAJ Chart live + légende externe
    effect(
      () => {
        if (this.viewType() !== "PIE") return;
        const canvas = this.canvasRef();
        if (!canvas || !this.chart) return;

        const labels = this.pieLabels();
        const values = this.values();
        const ds = this.chart.data.datasets[0] as ChartDataset<
          "doughnut",
          number[]
        >;
        const baseLabel = this.viewMode() === "CA" ? "Valeur" : "Unités";
        const newLabel =
          this.viewScope() === "PERTES" ? `${baseLabel} (pertes)` : baseLabel;

        const prevLabels = (this.chart.data.labels ?? []) as string[];
        const prevValues = (ds.data ?? []) as number[];

        this.chart.data.labels = labels;
        ds.data = values;
        ds.label = newLabel;

        if (!this.arrEq(values, prevValues)) {
          ds.backgroundColor = this.colors();
        }

        if (
          !this.arrEq(labels, prevLabels) ||
          !this.arrEq(values, prevValues)
        ) {
          this.chart.update(); // trigger generateLabels -> maj legendLabels
        }
      },
      { allowSignalWrites: true },
    );

    // (Re)build / destroy chart selon la vue et la dispo du canvas
    effect(
      () => {
        if (this.viewType() !== "PIE") {
          this.destroyChart(true);
          return;
        }
        const canvas = this.canvasRef();
        if (!canvas) {
          this.destroyChart(true);
          return;
        }
        if (!this.values().length) {
          this.destroyChart(true);
          return;
        }
        this.buildChart();
      },
      { allowSignalWrites: true },
    );
  }

  private destroyChart(clearLegend = false) {
    if (clearLegend) this.legendLabels.set([]);
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  private buildChart() {
    const canvas = this.canvasRef();
    if (!canvas) return;
    const ctx = canvas.nativeElement.getContext("2d");
    if (!ctx) return;

    this.destroyChart(true);

    const baseLabel = this.viewMode() === "CA" ? "Valeur" : "Unités";
    const label =
      this.viewScope() === "PERTES" ? `${baseLabel} (pertes)` : baseLabel;

    const config: ChartConfiguration<"doughnut", number[], string> = {
      type: "doughnut",
      data: {
        labels: this.pieLabels(),
        datasets: [
          {
            label,
            data: this.values(),
            backgroundColor: this.colors(),
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        resizeDelay: 120,
        plugins: {
          legend: {
            display: true, // utile pour déclencher generateLabels
            position: "right",
            labels: {
              usePointStyle: true,
              generateLabels: (c) => {
                const data = c.data as any;
                const labels = (data.labels || []) as string[];
                const values = (data.datasets?.[0]?.data || []) as number[];
                const colors = (data.datasets?.[0]?.backgroundColor ||
                  []) as string[];
                const total = values.reduce((a, b) => +a + +b, 0);
                const out = labels.map((lbl, i) => {
                  const val = +values[i] || 0;
                  const pct =
                    total > 0 ? ((val / total) * 100).toFixed(0) : "0";
                  return {
                    label: lbl,
                    percentage: pct,
                    color: String(colors[i] ?? "#999"),
                  };
                });
                this.legendLabels.set(out);
                return []; // pas de légende native
              },
            },
          },
          tooltip: {
            bodyFont: { size: 12 },
            titleFont: { size: 12 },
            callbacks: {
              label: (ctx) => {
                const val = ctx.parsed as number;
                const isCA = this.viewMode() === "CA";
                return ` ${ctx.label}: ${isCA ? this.fmtEUR.format(val) : this.fmtINT.format(val)}`;
              },
            },
          },
          title: { display: false },
        },
        layout: { padding: { top: 6, right: 6, bottom: 6, left: 6 } },
      },
    };
    this.chart = new Chart<"doughnut", number[], string>(ctx, config);
  }

  formatValue(v: number): string {
    return this.viewMode() === "CA"
      ? this.fmtEUR.format(v)
      : this.fmtINT.format(v);
  }

  protected readonly Math = Math;
}
